import { useEffect } from "react";
import { Box, Stack, Tab, Tabs, tabClasses, useMediaQuery, useTheme } from "@mui/material";

import Content from "../Layout/Content";
import LoadingOverlay from "../Shared/LoadingOverlay";
import NoSharedDocuments from "./NoSharedDocuments";
import SharedDocuments from "./SharedDocuments";
import UploadedDocuments from "./UploadedDocuments";
import { DocumentType, useDocuments } from "./hooks/useDocuments";
import { useDocumentUploadConfig } from "./hooks/useDocumentUploadConfig";
import { DocumentTabs, useDocumentsActiveTab } from "./hooks/useDocumentsActiveTab";
import { useAnalytics } from "hooks/useAnalytics";

const tabSx = {
    [`& .${tabClasses.root}`]: {
        textTransform: "none",
        fontSize: "1.4rem",
        fontWeight: 600,
    },
};

export default function Documents() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { track } = useAnalytics();
    const { activeTab, setActiveTab } = useDocumentsActiveTab();

    const { documents: sharedDocuments, isLoading: isLoadingShared } = useDocuments(DocumentType.Shared);
    const { documents: uploadedDocuments, isLoading: isLoadingUploads } = useDocuments(DocumentType.Uploads);
    const { config: uploadConfig, isLoading: isLoadingConfig } = useDocumentUploadConfig();

    useEffect(() => {
        track("page_view", { page_title: "Documents" });
    }, [track]);

    const isLoading = isLoadingShared || isLoadingUploads || isLoadingConfig;

    const handleUploadClick = () => setActiveTab(DocumentTabs.YourUploads);

    return (
        <Content dataTestIdSuffix="documents">
            {isLoading ? (
                <LoadingOverlay />
            ) : (
                <Stack p={isMobile ? 2 : 4} spacing={2}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, newValue: DocumentTabs) => setActiveTab(newValue)}
                        data-test-id="tabs_documents"
                        sx={tabSx}
                    >
                        <Tab label="Your Documents" data-test-id="tab_your_documents" />
                        <Tab label="Your Uploads" data-test-id="tab_your_uploads" />
                    </Tabs>

                    <Box pt={3}>
                        {activeTab === DocumentTabs.YourDocuments &&
                            (sharedDocuments.length ? (
                                <SharedDocuments documents={sharedDocuments} />
                            ) : (
                                <NoSharedDocuments onUploadClick={handleUploadClick} />
                            ))}

                        {activeTab === DocumentTabs.YourUploads && (
                            <UploadedDocuments documents={uploadedDocuments} uploadConfig={uploadConfig} />
                        )}
                    </Box>
                </Stack>
            )}
        </Content>
    );
}
